import React from "react";
import { Spinner } from "@chakra-ui/react";
import { FiTrendingUp, FiTrendingDown, FiMinus } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { cn } from "utils/helpers";
import { useOverallScores } from "../../hooks/useOverallScores";
import type { OverallSupplierScore, OverallCriteriaScore } from "../../types/meeting.type";

interface CurrentScoresModalProps {
  evalId: number;
  isOpen: boolean;
  rfxId: number;
  onClose: () => void;
}

const fmt = (val: number | null): string =>
  val !== null ? val.toFixed(1) : "—";

const DiffBadge: React.FC<{ diff: number | null }> = ({ diff }) => {
  if (diff === null) return <span className="text-gray-400">—</span>;

  const isPositive = diff > 0;
  const isNegative = diff < 0;
  const isNeutral = diff === 0;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium",
        isPositive && "text-green-600",
        isNegative && "text-red-600",
        isNeutral && "text-gray-400"
      )}
    >
      {isPositive && <FiTrendingUp size={12} />}
      {isNegative && <FiTrendingDown size={12} />}
      {isNeutral && <FiMinus size={12} />}
      {isPositive ? "+" : ""}
      {diff.toFixed(1)}
    </span>
  );
};

export const CurrentScoresModal: React.FC<CurrentScoresModalProps> = ({
  rfxId,
  evalId,
  isOpen,
  onClose,
}) => {
  const { data, isLoading } = useOverallScores(
    isOpen ? rfxId : undefined,
    isOpen ? evalId : undefined
  );

  const suppliers = data?.suppliers ?? [];

  // Collect unique criteria names across all suppliers
  const criteriaColumns: { id: number; name: string; weight: number }[] = [];
  if (suppliers.length > 0) {
    for (const cs of suppliers[0].criteria_scores) {
      criteriaColumns.push({
        id: cs.criteria_id,
        name: cs.criteria_name,
        weight: cs.criteria_weight,
      });
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Current Scores Overview"
      size="xxl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : suppliers.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          No score data available yet.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-200" />
              Pre-Meeting Avg
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-blue-500" />
              Current Consensus
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="current-scores-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 font-medium text-gray-700 min-w-[150px] sticky left-0 bg-gray-50">
                    Supplier
                  </th>
                  {criteriaColumns.map((c) => (
                    <th
                      key={c.id}
                      className="text-center px-2 py-2 font-medium text-gray-700 min-w-[100px]"
                    >
                      <div className="text-xs">{c.name}</div>
                      <div className="text-[10px] font-normal text-gray-400">{c.weight}%</div>
                    </th>
                  ))}
                  <th className="text-center px-3 py-2 font-semibold text-gray-900 min-w-[100px] border-l border-gray-200">
                    Weighted Total
                  </th>
                  <th className="text-center px-2 py-2 font-medium text-gray-700 min-w-[60px]">
                    Diff
                  </th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <SupplierRow
                    key={supplier.invite_id}
                    supplier={supplier}
                    criteriaColumns={criteriaColumns}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ── Supplier Row ───────────────────────────────── */

interface SupplierRowProps {
  criteriaColumns: { id: number; name: string; weight: number }[];
  supplier: OverallSupplierScore;
}

const SupplierRow: React.FC<SupplierRowProps> = ({ supplier, criteriaColumns }) => {
  const criteriaMap = new Map<number, OverallCriteriaScore>();
  for (const cs of supplier.criteria_scores) {
    criteriaMap.set(cs.criteria_id, cs);
  }

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="px-3 py-3 font-medium text-gray-900 sticky left-0 bg-white">
        {supplier.supplier_name}
      </td>
      {criteriaColumns.map((col) => {
        const cs = criteriaMap.get(col.id);
        return (
          <td key={col.id} className="px-2 py-3 text-center">
            <div className="space-y-0.5">
              <div className="text-xs text-gray-400" title="Pre-meeting average">
                {fmt(cs?.pre_meeting_average ?? null)}
              </div>
              <div
                className={cn(
                  "text-sm font-medium",
                  cs?.consensus_score !== null && cs?.consensus_score !== undefined
                    ? "text-blue-700"
                    : "text-gray-300"
                )}
                title="Consensus score"
              >
                {cs?.consensus_score !== null && cs?.consensus_score !== undefined
                  ? cs.consensus_score
                  : "—"}
              </div>
            </div>
          </td>
        );
      })}
      <td className="px-3 py-3 text-center border-l border-gray-200">
        <div className="space-y-0.5">
          <div className="text-xs text-gray-400" title="Pre-meeting weighted average">
            {fmt(supplier.pre_meeting_weighted_average)}
          </div>
          <div className="text-sm font-semibold text-gray-900" title="Consensus weighted total">
            {fmt(supplier.consensus_weighted_total)}
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-center">
        <DiffBadge diff={supplier.diff} />
      </td>
    </tr>
  );
};
